<template>
  <div class="dcb-faq">
    <Heading
      tag="h2"
      align="center"
      class="dcb-teaser__title"
      size="md"
      spaces-bottom
    >
      Häufig gestellte Fragen
    </Heading>

    <AccordionItem
      v-for="(faq, key) in faqs"
      :key="key"
      @update:is-open="(args) => accordionToggle(args, slugify(faq.title))"
    >
      <template #title>
        <h3>{{ faq.title }}</h3>
      </template>
      <template #content>
        <div v-html="faq.content" />
      </template>
    </AccordionItem>
  </div>
</template>

<script setup lang="ts">
import { AccordionItem, Heading, slugify } from '@i22team/td-longtail-vue-components';
import { tealiumLink } from '~/lib/tealium';

const props = defineProps({
  withLottery: { type: Boolean, default: false },
});

const faqs = [
  {
    title:
      'Wie nutze ich „Zahl einfach per Handyrechnung“ als Bezahlmethode für App- & In-App-Käufe, bei Streaming-Diensten und anderen Anbietern?',
    content:
      'Zur Bezahlung von Apps und In-App-Käufen musst du Handyrechnung als Zahlungsmethode im App Store oder Play Store hinterlegen und an die oberste Stelle setzen. Die <a class="link" href="#dcb-instructions">Anleitung</a> findest du oben.' +
      '<br /><br />' +
      'Zum Bezahlen von Streaming-Abos und anderen Diensten wie Parktickets, EV-Charging, u.ä., entscheidest du dich im Bestellprozess bei der Auswahl der Zahlungsmethoden für die Handyrechnung.',
  },

  {
    title:
      'Muss ich mich für das Bezahlen per Handyrechnung registrieren oder Bankdaten hinterlegen?',
    content:
      'Nein, das ist nicht erforderlich. Die Abrechnung erfolgt über deine monatliche Mobilfunkrechnung bzw. dein Prepaid-Guthaben.',
  },
  {
    title: 'Wie erfolgt die Abrechnung bei „Zahl einfach per Handyrechnung“?',
    content: 'Vertragskunden zahlen mit ihrer nächsten monatlichen Handyrechnung.',
  },
  {
    title: 'Welche Produkte können mit „Zahl einfach per Handyrechnung“ gekauft werden?',
    content:
      'Fast alles, was digital zu erwerben ist. Es können zum Beispiel Apps, Spiele, Musik, Filme, E-Books und Tickets per Handyrechnung bezahlt werden. Auch Fahrkarten oder Parkscheine lassen sich auf diese Weise einfach, sicher und schnell kaufen. Empfehlungen für Apps findest du auf der Infoseite zu <a href="/zahlung-per-handyrechnung#partner" class="link">„Zahl einfach per Handyrechnung“</a>.',
  },
  {
    title: 'Was ist eine Drittanbietersperre und wie kann ich meine Drittanbietersperre aufheben? ',
    content: `Über eine Drittanbietersperre ist die Abrechnung aller Drittanbieter-Dienste (z. B. App-Stores, Fahrkarten, Parkscheine,…) über die Mobilfunkrechnung gesperrt. Du kannst Drittanbieter-Dienste über deinen Mobilfunk-Vertrag sperren und entsperren. Übrigens können auch nur einzelne Drittanbieter (z. B. nur der AppStore) entsperrt werden. Dazu gehe bitte wie folgt vor:
    <ul class="list">
    <li class="list__element--numbered">Loggen dich im <a href="https://cap.telekom.de/trinityspace/ui/auth?module=trinityspace&page=barringprofile" target="_blank" rel="noopener" class="link link--external">Kundencenter <span class="sr-only">(Der Link wird in einem neuen Tab geöffnet)</span></a> ein.</li>
    <li class="list__element--numbered">Die Sperrkonfiguration öffnet sich und du kannst über den Button "Bearbeiten" die Drittanbieter sperren oder entsperren.</li>
</ul>`,
  },
];

if (props.withLottery) {
  faqs.push({
    title: 'Was muss ich tun, um die Gutschrift aus der Aktion „5 € Gutschrift“ zu erhalten?',
    content:
      'Nachdem du im Aktionszeitraum nach deiner Registrierung deine ersten Käufe mit „Zahl einfach per Handyrechnung“ getätigt hast, erfolgt die Gutschrift in Höhe der Käufe (bis insgesamt max. 5 €) nach Ablauf einer etwaigen 14-tägigen Widerrufsfrist auf der nächsten Mobilfunkrechnung. Ein initialer „erster Kauf“ liegt vor, wenn du in den 3 Monaten vor Kampagnenstart (01.04.2026) keinen Kauf per Handyrechnung getätigt hast.',
  });
  faqs.push({
    title: 'Wer ist teilnahmeberechtigt für die Aktion „5 € Gutschrift“?',
    content:
      'Teilnahmeberechtigt sind alle Telekom Mobilfunkkunden mit einem MagentaMobil Laufzeitvertrag, die in den 3 Monaten vor Kampagnenstart (01.04.2026) keinen Kauf per Handyrechnung getätigt haben. Nicht teilnahmeberechtigt sind Prepaid-Kunden und Laufzeitverträge anderer Tarife (u.a. congstar).',
  });
  faqs.push({
    title: 'Wo sehe ich die Gutschrift?',
    content:
      'Du findest die Gutschrift auf deiner Mobilfunkrechnung unter „Forderungen aus Leistungen anderer Anbieter“ als „Verrechnung Telekom Multi-Guthaben“.',
  });
  faqs.push({
    title:
      'Ich habe „Zahl einfach per Handyrechnung“ im Handy als Bezahlmethode eingerichtet, trotzdem werden meine Käufe über andere hinterlegte Zahlungsmittel abgerechnet. Was muss ich tun?',
    content:
      'Richte „Handyrechnung“ als primäres Zahlverfahren ein (erkennbar daran, dass es oben als erste Zahlungsmethode erscheint). Dafür musst du ggf. vorhandene Zahlungsmittel entfernen, bzw. die Reihenfolge der Zahlungsmethoden ändern und „Handyrechnung“ an die oberste Stelle ziehen.',
  });
}

const accordionToggle = (isOpen: boolean, title: string) => {
  tealiumLink(`content.button.faq.${isOpen ? 'open' : 'close'}.${title}`);
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.dcb-faq {
  margin-bottom: $grid-base * 5;
}
</style>
